/* global GM_STATE */
/**
 * Presentation half of the size selector, shared by the PDP's <x-product-selector> and the
 * wishlist drawer. It reports selections through onSelect and touches no global state, so a
 * consumer that must not re-point the page (the drawer) simply omits that prop.
 *
 * Kept in its own module rather than exported from product-selector.js: application code is not
 * split (webpack.config.js only splits the preact and swiper vendors), so importing that file
 * would copy its `register()` call into a second bundle and redefine x-product-selector.
 */
import { useEffect, useMemo, useState } from 'preact/hooks'
import Icon from 'snippets/icon/icon'
import clsx from 'clsx'
import { getStockIndicators, matchStockIndicator } from 'libs/helpers/stock-indicators'
import { bandDisplayName, bandLabel, sortBands } from 'libs/helpers/age-bands'
import { sizeUnitValue, sizeUnitLabel } from 'helpers/size-units.mjs'

const SIZE_CATEGORY_KEYS = {
  BABY: 'Baby',
  TODDLER: 'Toddler',
  '3-8_YEARS': '3-8 Yrs',
  '8+_YEARS': '8+ Yrs'
}

/**
 * `value` matches the variant title and the utopia metafields; `header` matches checkHeader()'s
 * keys. Only `label` is shown, so it can differ from both — the UK tab reads "AU/UK".
 */
export const sizeTypes = [
  { value: 'UK', header: 'uk', label: sizeUnitLabel('UK') },
  { value: 'US', header: 'us', label: sizeUnitLabel('US') },
  { value: 'EURO', header: 'eu', label: sizeUnitLabel('EURO') },
  { value: 'CM', header: 'cm', label: sizeUnitLabel('CM') }
]

const DEFAULT_WIDTH_IF_PRESENT = 'E'
/** Widths shown before "Show more widths" is needed — one full 3-column row. */
const WIDTHS_PER_ROW = 3
/** Fallbacks only — a linked product is named when Liquid could resolve a title for it. */
const SMALLER_SIZES_LABEL = 'View Younger Sizes'
const BIGGER_SIZES_LABEL = 'View Older Sizes'

const siblingLabel = (title, fallback) => (title ? `View ${title}` : fallback)
/** Layout A/B treatment, handed in by Liquid as data-size-layout. Anything else means stacked. */
const CONSOLIDATED_LAYOUT = 'consolidated'
const ALL_SIZES_TAB = 'All Sizes'

/** Dispatched on window by ProductForm when add-to-cart is pressed with no size chosen. */
export const SIZE_REQUIRED_EVENT = 'size-selection-required'
const SIZE_GUIDE_ID = 'size-guide'
/** Rendered by product-fit-tips.liquid inside the size guide accordion, only when advice resolves. */
const WIDTH_ADVICE_ID = 'width-advice'
/** VisiblityElement.EVENT_SHOW — opens the x-modal whose data-id matches detail.target. */
const SHOW_EVENT = 'show'

/**
 * Opens the size guide accordion and scrolls to `targetId` inside it. The enclosing product form
 * preventDefaults every click to drive its own variant handling, which cancels anchor navigation,
 * so the open and the scroll have to be explicit.
 */
const openSizeGuideAt = (event, targetId = SIZE_GUIDE_ID) => {
  event.preventDefault()
  event.stopPropagation()
  const panel = document.getElementById(SIZE_GUIDE_ID)
  if (!panel) return
  document.dispatchEvent(new CustomEvent(SHOW_EVENT, { detail: { target: SIZE_GUIDE_ID } }))
  const target = document.getElementById(targetId) ?? panel
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** The "Size guide" / "Width advice" links share one look; href is kept for semantics only. */
const SizeGuideLink = ({ icon, iconClass, label, targetId }) => (
  <a
    href={`#${targetId}`}
    onClick={(event) => openSizeGuideAt(event, targetId)}
    className={'flex shrink-0 flex-row items-center gap-1'}
  >
    <Icon id={icon} className={clsx('h-5 w-5', iconClass)} />
    <span className={'paragraph-sm font-medium underline underline-offset-4'}>{label}</span>
  </a>
)

export const getSizeGroupFromRange = (size) => {
  if (size < 20) {
    return SIZE_CATEGORY_KEYS.BABY
  }
  if (size >= 20 && size < 28) {
    return SIZE_CATEGORY_KEYS.TODDLER
  }
  if (size >= 28 && size < 35) {
    return SIZE_CATEGORY_KEYS['3-8_YEARS']
  }
  if (size >= 35) {
    return SIZE_CATEGORY_KEYS['8+_YEARS']
  }
}

/**
 * Shared with both cart surfaces so a line's size label can never disagree with the pill it was
 * added from. Kept as a local name because the call sites below read as `handleGetTypeValue`.
 */
const handleGetTypeValue = (type, size) => sizeUnitValue(size, type)

/**
 * Parses the product variant data into groups based on its category and or width
 *
 * DataStructure
 *
 * A) Without widths
 * {
 *  [category: string] {
 *    [id] : string
      [name] : string
      [available] : boolean
      [age_group] : string
      [cm] : string
      [uk] : string
      [us] : string
      [eu] : string
 *  }
 * }
 *  *
 * B) With widths
 * {
 *  [category: string] {
 *    [width: string] {
 *    [id] : string
      [name] : string
      [available] : boolean
      [age_group] : string
      [cm] : string
      [uk] : string
      [us] : string
      [eu] : string }
    }
 * }
 * @param {*} variants
 * @returns
 */
const sizeGroups = (variants) => {
  const s = variants.reduce((acc, v) => {
    const group = v.age_group
    if (v?.width) {
      if (acc[group]) {
        if (acc[group][v.width]) {
          acc[group][v.width].push(v)
          return acc
        }
      }
      acc[group] = {
        ...acc[group],
        [v.width]: [v]
      }
      return acc
    }

    if (acc[group]) {
      acc[group].push(v)
      return acc
    }
    acc[group] = [v]
    return acc
  }, {})

  return s
}

/**
 * The options for one age band at the currently selected width. sizeGroups() nests bands by width
 * only when the product has a Width option, so a band is otherwise a flat array.
 */
const bandOptions = (ageGroup, selected) => {
  if (!ageGroup) return []
  const width = !selected?.width ? selected?.tmpWidth : selected?.width
  const options = width?.length > 0 ? ageGroup[width] : ageGroup
  return options?.length > 0 ? options : []
}

export const parseSizeType = (c, type) => {
  return c.name.split('/').filter((t) => t.includes(type))
}

export const getSizeFromTitle = (row, type) => {
  const inputType = type === 'CM' ? 'EURO' : type
  const input = Array.isArray(row) ? row.pop() : row
  const name = parseSizeType(input, inputType)
  if (!name.length) {
    return '-'
  }
  const value = name.pop().trimEnd().split(' ').pop()
  if (type === 'CM') return (parseFloat(value) * 2.54).toFixed(2)
  return value
}

/**
 *
 * @param {*} param0
 * @returns
 */
const AvailableOption = ({ size, handleSetSelectedCallback, isSelected, productId, stockTier }) => {
  const handleSelectSize = () => {
    handleSetSelectedCallback({
      id: size.id,
      name: size.name,
      value: size.value,
      width: size?.width,
      variant: size.variant,
      unavailable_stock: false,
      notify: { inventory: size.inventory, available: true }
    })
  }
  return (
    <div
      data-dl-track="toggle_variant_pdp"
      data-dl-attribute="item_size"
      data-dl-value={size.value}
      data-dl-product-id={productId}
      onClick={handleSelectSize}
      class={`relative flex h-12 min-w-12 xl:min-w-18 w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-solid ${
        isSelected ? 'bg-primary-blue text-white' : 'border-[#E0DED8]'
      } `}
    >
      <span>{size.value}</span>
      {/* Absolute so it rides inside the rounded border instead of fighting it. */}
      {stockTier && (
        <span
          class="absolute inset-x-0 bottom-0 h-px"
          style={{ backgroundColor: stockTier.colour }}
          aria-hidden="true"
        />
      )}
    </div>
  )
}

const UnavailableOption = ({ size, handleSetSelectedCallback, isSelected, productId }) => {
  const handleSelectSize = () => {
    handleSetSelectedCallback({
      id: size.id,
      name: size.name,
      width: size?.width,
      value: size.value,
      variant: size.variant,
      unavailable_stock: true,
      notify: { inventory: 0, available: false }
    })
  }
  return (
    <div
      data-dl-track="toggle_variant_pdp"
      data-dl-attribute="item_size"
      data-dl-value={size.value}
      data-dl-product-id={productId}
      onClick={handleSelectSize}
      class={`size-option-soldout relative flex h-12 min-w-16 w-full grow cursor-pointer flex-col items-center justify-center rounded-md border border-solid bg-white text-[#E0DED8] ${
        isSelected ? 'border-black' : 'border-[#E0DED8]'
      } `}
    >
      <span>{size.value}</span>
    </div>
  )
}

const SelectorSize = ({ type, option, handleSetSelectedCallback, selected, productId, stockIndicators }) => {
  const size = useMemo(() => {
    const name = getSizeFromTitle(option, type)
    const id = option.id
    const available = option.available
    const getSizeValue = handleGetTypeValue(type, option)
    const value = !getSizeValue ? option.name : getSizeValue
    return { name, id, available, width: option.width, variant: option.name, value, inventory: option.inventory }
  }, [type, selected, option])

  if (!size.value) return <></>

  if (size.available === false) {
    return (
      <UnavailableOption
        productId={productId}
        size={size}
        handleSetSelectedCallback={handleSetSelectedCallback}
        isSelected={size.id === selected.id}
      />
    )
  }
  return (
    <AvailableOption
      productId={productId}
      size={size}
      handleSetSelectedCallback={handleSetSelectedCallback}
      isSelected={size.id === selected.id}
      stockTier={matchStockIndicator(size.inventory, stockIndicators)}
    />
  )
}

function SelectCountrySizeCategory({ validHeaders, current, setTypeCallback }) {
  return (
    <div className={'flex flex-row gap-2'}>
      {sizeTypes
        .filter((s) => validHeaders.includes(s.header))
        .map((sizeType) => {
          return (
            <div
              key={sizeType.value}
              onClick={() => setTypeCallback(sizeType.value)}
              className={clsx(
                'paragraph-md m-auto cursor-pointer font-medium underline transition-colors duration-300',
                sizeType.value === current ? 'text-[#1E1E1E]' : 'text-[#5F5C59]'
              )}
            >
              {sizeType.label}
            </div>
          )
        })}
    </div>
  )
}

const SelectSizeHeader = ({
  validHeaders,
  selected,
  sizeLabel,
  callback,
  type,
  pageType,
  sizeError,
  showSizeGuide = true
}) => {
  const isGiftCard = pageType === 'gift-card'

  let selectHeading = 'Size'
  if (isGiftCard) {
    selectHeading = 'Select Amount:'
  } else if (sizeError) {
    selectHeading = 'Select Size'
  }

  return (
    <div className={'flex h-11 flex-row items-center justify-between md:h-auto pb-4 border-b border-solid border-[#F1EFED]'}>
      <div className="flex flex-row items-center gap-2">
        <p className={clsx('paragraph-md font-bold', !isGiftCard && sizeError && 'text-system-error')}>
          {selectHeading}:
        </p>
        {!isGiftCard ? (
          <SelectCountrySizeCategory validHeaders={validHeaders} current={type} setTypeCallback={callback} />
        ) : (
          <>
            <input name="gift-card-total" type="hidden" value={selected.value} />
          </>
        )}
      </div>
      {!isGiftCard && showSizeGuide && <SizeGuideLink icon="size" label="Size guide" targetId={SIZE_GUIDE_ID} />}
    </div>
  )
}

const checkHeader = (parseSizeDataObjects) => {
  const keys = ['cm', 'us', 'eu', 'uk']
  const object = parseSizeDataObjects[0]
  const arr = keys.reduce((acc, key) => {
    if (object[key] !== undefined) {
      acc.push(key)
    }
    return acc
  }, [])

  return arr
}

/** Shared by both size treatments and the width row, so their borders and spacing line up. */
const SECTION_ROW_CLASS = 'flex flex-col  gap-5 py-4 '

/**
 * `aside` sits on the title row's far end, e.g. the width row's "Width advice" link. `caption`
 * switches the heading to the quieter band wording the stacked rows use, and `footer` is for
 * anything that must sit outside the grid — sibling links are navigation, not options.
 */
const SectionRow = ({ children, title, aside, caption = false, band, footer, divider = false }) => {
  return (
    <div
      class={clsx(
        caption
          ? 'flex flex-col gap-3 py-4 border-b border-solid border-[#F1EFED] last:border-b-0'
          : SECTION_ROW_CLASS,
        divider && 'border-t border-solid border-[#F1EFED]'
      )}
      data-role="section-row"
      data-size-band={band}
    >
      {caption ? (
        <div data-role="band-caption" className={'paragraph-xs text-primary-black font-bold'}>
          {title}
        </div>
      ) : aside ? (
        <div className={'flex flex-row items-center justify-between'}>
          <div className={'font-bold paragraph-md'}>{title}:</div>
          {aside}
        </div>
      ) : (
        <div className={'font-bold paragraph-md'}>{title}</div>
      )}
      <div data-role="option-grid" className={'flex flex-wrap gap-2 justify-start'}>
        {children}
      </div>
      {footer}
    </div>
  )
}

const SelectSizeOptions = ({ type, ageCategory, callback, selected, linkedOptions, productId, stockIndicators }) => {
  const getFirstAvailableIndex = useMemo(() => {
    let index = 0
    for (const category of ageCategory.categories) {
      const ageGroup = ageCategory.groups[category]
      if (!ageGroup) {
        index += 1
        continue
      }
      break
    }
    return index
  }, [])

  return (
    <div class={'flex flex-col'}>
      {ageCategory?.categories?.map((category, index) => {
        const displayOptions = bandOptions(ageCategory?.groups[category], selected)
        if (!displayOptions.length) return <></>
        const showSmaller = index === getFirstAvailableIndex && !!linkedOptions?.small
        const showBigger = index >= ageCategory.categories.length - 1 && !!linkedOptions?.bigger

        return (
          <SectionRow
            band={category}
            caption
            title={bandLabel(category)}
            footer={
              (showSmaller || showBigger) && (
                <div className={'flex flex-row gap-2'}>
                  {showSmaller && (
                    <SiblingSizeLink
                      handle={linkedOptions.small}
                      label={siblingLabel(linkedOptions.smallTitle, SMALLER_SIZES_LABEL)}
                    />
                  )}
                  {showBigger && (
                    <SiblingSizeLink
                      handle={linkedOptions.bigger}
                      label={siblingLabel(linkedOptions.biggerTitle, BIGGER_SIZES_LABEL)}
                    />
                  )}
                </div>
              )
            }
          >
            {displayOptions?.map((option) => {
              return (
                <div>
                  <SelectorSize
                    productId={productId}
                    selected={selected}
                    type={type}
                    option={option}
                    handleSetSelectedCallback={callback}
                    stockIndicators={stockIndicators}
                  />
                </div>
              )
            })}
          </SectionRow>
        )
      })}
    </div>
  )
}

const SizeBandTabs = ({ bands, active, setActiveCallback }) => {
  return (
    <div class={'flex flex-row justify-between rounded-full bg-neutrals-base p-2 h-16'}>
      {[ALL_SIZES_TAB, ...bands].map((band) => {
        const isActive = band === active
        return (
          <div
            key={band}
            data-band={band}
            onClick={() => setActiveCallback(band)}
            class={clsx(
              'relative flex w-full cursor-pointer flex-col justify-center p-2 text-center h-12',
              isActive && 'rounded-full bg-white'
            )}
          >
            {band === ALL_SIZES_TAB ? (
              <p class={clsx('paragraph-sm font-medium leading-none', isActive ? 'opacity-100' : 'opacity-55')}>{band}</p>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <p class={clsx('paragraph-xs font-medium', isActive ? 'opacity-100' : 'opacity-55')}>
                  {bandDisplayName(band)}
                </p>
                <p class={clsx('text-[9px] font-medium leading-none', isActive ? 'opacity-100' : 'opacity-55')}>
                  {bandLabel(band, { short: true })}
                </p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const SiblingSizeLink = ({ handle, label }) => {
  /** The enclosing product form preventDefaults every click, so navigate explicitly. */
  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    window.location.href = `/products/${handle}`
  }
  return (
    <a
      href={`/products/${handle}`}
      onClick={handleClick}
      class="paragraph-sm flex h-12 w-fit items-center justify-center rounded-md border border-solid border-[#F1EFED] px-4 font-medium"
    >
      {label}
    </a>
  )
}

/**
 * Consolidated treatment: every band flattened into one grid, scoped by filter tabs.
 *
 * Tab state is local, so switching tabs never touches the selected variant — the hidden id input
 * and the add-to-cart button keep whatever was chosen even when that chip is filtered out of view.
 */
const SelectSizeOptionsConsolidated = ({
  type,
  ageCategory,
  callback,
  selected,
  linkedOptions,
  productId,
  stockIndicators
}) => {
  const [activeBand, setActiveBand] = useState(ALL_SIZES_TAB)

  /** Bands with nothing at the current width are dropped, matching how stacked skips their row. */
  const visibleBands = (ageCategory?.categories ?? []).filter(
    (category) => bandOptions(ageCategory?.groups[category], selected).length
  )
  /** A width change can empty the active band, so fall back rather than render an empty grid. */
  const currentBand = visibleBands.includes(activeBand) ? activeBand : ALL_SIZES_TAB
  const shownBands = currentBand === ALL_SIZES_TAB ? visibleBands : [currentBand]

  const options = shownBands.flatMap((band) => bandOptions(ageCategory?.groups[band], selected))
  if (!options.length) return <></>

  /** Sibling links mean "this product stops here", so they only belong beside the outermost band. */
  const showSmaller = !!linkedOptions?.small && shownBands.includes(visibleBands[0])
  const showBigger = !!linkedOptions?.bigger && shownBands.includes(visibleBands[visibleBands.length - 1])

  return (
    <div class={SECTION_ROW_CLASS}>
      {visibleBands.length > 1 && (
        <SizeBandTabs bands={visibleBands} active={currentBand} setActiveCallback={setActiveBand} />
      )}
      <div data-role="option-grid" className={'grid grid-cols-5 md:grid-cols-6 3xl:grid-cols-7 flex-wrap gap-2 justify-start'}>
        {options.map((option) => {
          return (
            <div key={option.id} className="w-full">
              <SelectorSize
                productId={productId}
                selected={selected}
                type={type}
                option={option}
                handleSetSelectedCallback={callback}
                stockIndicators={stockIndicators}
              />
            </div>
          )
        })}
      </div>
      {(showSmaller || showBigger) && (
        <div className={'flex flex-row gap-2'}>
          {showSmaller && (
            <SiblingSizeLink
              handle={linkedOptions.small}
              label={siblingLabel(linkedOptions.smallTitle, SMALLER_SIZES_LABEL)}
            />
          )}
          {showBigger && (
            <SiblingSizeLink
              handle={linkedOptions.bigger}
              label={siblingLabel(linkedOptions.biggerTitle, BIGGER_SIZES_LABEL)}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Folds a variant_text_overrides map to lower case at both levels. The entries are merchant-typed,
 * so "width"/"d" has to match Shopify's "Width"/"D"; doing it once here keeps every lookup a
 * plain read.
 */
export const normaliseTextOverrides = (overrides) =>
  Object.entries(overrides ?? {}).reduce((byOption, [option, values]) => {
    byOption[String(option).toLowerCase()] = Object.entries(values ?? {}).reduce((byValue, [value, label]) => {
      byValue[String(value).toLowerCase()] = label
      return byValue
    }, {})
    return byOption
  }, {})

/** Display only — the raw option value stays on every selection hook and dataLayer attribute. */
export const optionLabel = (overrides, option, value) =>
  overrides?.[option.toLowerCase()]?.[String(value ?? '').toLowerCase()] ?? value

const removeWidthKey = (input) => {
  const splitLastIndex = input.lastIndexOf('/')
  return input.slice(0, splitLastIndex)
}

const SelectWidthOptions = ({
  parseSizeDataObjects,
  selected,
  handleSetSelected,
  widths,
  type,
  productId,
  showWidthAdvice = false,
  textOverrides = {}
}) => {
  const handleUpdatedWidthCallback = (evt) => {
    const { dataset } = evt.currentTarget
    const getGetMatchingWidth = parseSizeDataObjects.filter((size) => size.width.includes(dataset.width))
    const findMatchingSizes = getGetMatchingWidth.find((o) => {
      return removeWidthKey(selected.variant).trim() === removeWidthKey(o.name).trim()
    })

    if (!findMatchingSizes) {
      handleSetSelected({
        id: '',
        name: '',
        width: dataset.width,
        firstRender: true,
        variant: '',
        unavailable_stock: false
      })
      return
    }
    const matchedSize = !findMatchingSizes ? parseSizeDataObjects[0] : findMatchingSizes
    const name = getSizeFromTitle(matchedSize, type)
    const unavailable = !matchedSize.available

    handleSetSelected({
      id: matchedSize.id,
      name,
      width: matchedSize.width,
      firstRender: false,
      variant: matchedSize.name,
      unavailable_stock: unavailable,
      notify: {}
    })
  }

  /** Local state only — expanding the row must never disturb the selected variant. */
  const [showAllWidths, setShowAllWidths] = useState(false)
  const hasOverflow = (widths?.length ?? 0) > WIDTHS_PER_ROW
  const visibleWidths = hasOverflow && !showAllWidths ? widths.slice(0, WIDTHS_PER_ROW) : widths

  const widthAdvice = showWidthAdvice ? (
    <SizeGuideLink icon="arrow-drag" iconClass="-rotate-45" label="Width Advice" targetId={WIDTH_ADVICE_ID} />
  ) : null

  return (
    <SectionRow title="Width" aside={widthAdvice} divider>
      {/* Fixed 3-column grid, so each option is a third of the row regardless of how many show. */}
      <div class={'grid w-full grid-cols-3 gap-2'}>
        {visibleWidths?.map((w) => {
          const hasStock = !!w?.hasStock
          const isSelected = w?.width === selected.width
          if (!hasStock) {
            return (
              <div
                data-width={w.width}
                class={`size-option-soldout flex h-12 w-full cursor-pointer flex-col items-center justify-center rounded-md border border-solid bg-system-error text-white
                ${isSelected ? 'border-black' : 'border-system-error'}
                `}
                onClick={handleUpdatedWidthCallback}
                data-dl-track="toggle_variant_pdp"
                data-dl-attribute="item_width"
                data-dl-value={w.width}
                data-dl-product-id={productId}
              >
                <span>{optionLabel(textOverrides, 'Width', w.width)}</span>
              </div>
            )
          }

          return (
            <div
              data-width={w?.width}
              class={`flex h-12 w-full cursor-pointer flex-col items-center justify-center rounded-md border border-solid overflow-hidden ${
                isSelected ? 'bg-primary-blue text-white' : 'border-[#E0DED8] '
              } `}
              onClick={handleUpdatedWidthCallback}
              data-dl-track="toggle_variant_pdp"
              data-dl-attribute="item_width"
              data-dl-value={w.width}
              data-dl-product-id={productId}
            >
              {optionLabel(textOverrides, 'Width', w?.width)}
            </div>
          )
        })}
      </div>
      {hasOverflow && !showAllWidths && (
        <button
          type="button"
          onClick={() => setShowAllWidths(true)}
          class="paragraph-sm w-fit font-medium underline underline-offset-4"
        >
          Show more widths
        </button>
      )}
    </SectionRow>
  )
}

/**
 *
 * If the product variant options has defaultValue (In our case variant width E) has a value and E has stock available
 * Re-sort the array to place E first and set width value to E
 *
 * If E does not exist or has no stock use the default logic of having the options with no stock to the end of the list.
 *
 * @param {{width: string, widthOptions: {width: string, hasStock: boolean}[]}} productsWidths
 * @param {string} defaultValue
 * @returns {{width: string, widthOptions: {width: string, hasStock: boolean}[]}}
 */
const handleHasDefaultValue = (productsWidths, defaultValue) => {
  const hasDefault = productsWidths.widthOptions.find((option) => option.width === defaultValue)
  if (!hasDefault) return productsWidths

  if (!hasDefault.hasStock) return productsWidths

  /** Disable the sorting of the width values
   *
   */
  const sortedWidthOptions = productsWidths.widthOptions.sort((a, b) => a.width.localeCompare(b.width))
  return {
    width: defaultValue,
    widthOptions: sortedWidthOptions
  }
}

/**
 * If we have a multiple Width options available
 * Check to see which width collection has stock
 */
const handleDisplayStockOption = (data) => {
  const getWidthsAvailability = data.reduce((acc, d) => {
    if (!d?.width) {
      return acc
    }
    if (acc[d.width]) {
      acc[d.width].push(d)
      return acc
    }
    acc[d.width] = [d]
    return acc
  }, {})

  const widthOptions = Object.keys(getWidthsAvailability)

  if (!widthOptions.length) {
    return {
      width: '',
      widthOptions: []
    }
  }

  if (widthOptions.length === 1) {
    return {
      width: widthOptions[0],
      widthOptions: widthOptions.map((w) => ({ width: w, widthOptions: [{ width: w, hasStock: true }] }))
    }
  }

  const widthStockObjects = widthOptions.map((w) => {
    const hasStock = getWidthsAvailability[w].some((size) => size.available)
    return { width: w, hasStock }
  })

  const widthWithStock = widthStockObjects.find((s) => s.hasStock)
  if (!widthWithStock) {
    return { width: widthOptions[0], widthOptions: widthOptions.map((w) => ({ width: w, hasStock: true })) }
  }
  const sortedWidthOptions = widthStockObjects.sort((a, b) => b.hasStock - a.hasStock)

  return { width: widthWithStock.width, widthOptions: sortedWidthOptions }
}

/**
 * The value a variant's pill shows in the given unit — `SelectorSize` derives its label the same
 * way, so a summary built from this can never disagree with the grid behind it. Mirrors how
 * mini-cart.js resolves a cart line's size, but off the variant's own unit fields rather than by
 * re-parsing the title.
 */
export const sizeValueFor = (variant, type) =>
  variant ? handleGetTypeValue(type || 'US', variant) || variant.name : null

const emptySelection = (defaultWidth) => ({
  id: '',
  name: '',
  width: '',
  tmpWidth: defaultWidth,
  firstRender: true,
  variant: '',
  unavailable_stock: false,
  value: ''
})

/** The `selected` shape the grid and width row read, derived from one parsed variant row. */
const selectionFrom = (match, type, defaultWidth) => ({
  id: match.id,
  name: getSizeFromTitle(match, type),
  width: match.width ?? '',
  tmpWidth: defaultWidth,
  firstRender: false,
  variant: match.name,
  unavailable_stock: !match.available,
  value: handleGetTypeValue(type, match) || match.name
})

/**
 * The size selector proper: unit tabs, age-band size grid and the width row.
 *
 * Selection state is internal, as it always was, but `value` syncs a variant id in from the
 * outside and `onChange` reports state out. That pairing is what lets the wishlist drawer bind
 * the control to whichever list row is active while the PDP keeps its own uncontrolled behaviour
 * by passing no `value` at all.
 *
 * @param {object[]} variants Parsed variant rows: { id, name, available, inventory, age_group, uk, us, eu, cm, width? }
 * @param {string|null} [value] Variant id to adopt. Pass it (null included) to control the component; omit to not.
 * @param {string} [initialVariantId] Variant id to open on, seed only. For an uncontrolled consumer (the PDP,
 *   resolving `?variant=`) that must not be re-pointed afterwards. Ignored when `value` is passed.
 * @param {(selected: object, notify?: object) => void} [onChange] Every state change. `notify` is set only for a
 *   user-made selection, so a consumer that must not react to a unit switch can test for it.
 * @param {string} [sizeRequiredEvent] Window event that puts the header into its error state. Omit to not listen.
 * @param {(type: string) => void} [onTypeChange] The active unit tab, on mount and on every switch.
 * @param {boolean} [showWidthAdvice] Adds a "Width advice" link to the width row. The PDP sets it from whatever
 *   width-advice.liquid resolves off the width_advice metaobject; the row itself only renders when the
 *   product has more than one width.
 */
export const SizeSelector = ({
  variants,
  value,
  initialVariantId,
  onChange,
  initialType,
  layout,
  pageType,
  productId,
  linkedOptions,
  showSizeGuide = true,
  showWidthAdvice = false,
  textOverrides,
  sizeRequiredEvent,
  onTypeChange,
  className
}) => {
  const [type, setType] = useState(initialType || 'US')
  const [sizeError, setSizeError] = useState(false)
  const optionOverrides = useMemo(() => normaliseTextOverrides(textOverrides), [textOverrides])

  const productWidths = useMemo(() => handleHasDefaultValue(handleDisplayStockOption(variants), DEFAULT_WIDTH_IF_PRESENT), [])

  const findVariant = (id) => (id ? variants.find((variant) => String(variant.id) === String(id)) : undefined)

  /**
   * Seeded here rather than by the sync effect below, which cannot win the race: the [type]
   * effect reads `selected` from the same render's closure, so whatever it writes on mount
   * overwrites an effect-applied selection and leaves the grid blank over a filled summary.
   */
  const [selected, setSelected] = useState(() => {
    const seed = findVariant(value ?? initialVariantId)
    return seed ? selectionFrom(seed, type, productWidths.width) : emptySelection(productWidths.width)
  })

  /**
   * `notify` rides along on the payload so only the three user-driven call sites report outwards;
   * the unit-switch effect below reuses the same setter and must stay silent.
   */
  const handleSetSelected = (next) => {
    const { notify, ...state } = next
    if (state?.id) setSizeError(false)
    const resolved = { ...state, firstRender: false }
    setSelected(() => resolved)
    onChange?.(resolved, notify && state?.id ? notify : undefined)
  }
  const handleSetType = (t) => setType(t)

  /** Reported on mount too: the opening unit is the one an immediate add-to-cart would use. */
  useEffect(() => {
    onTypeChange?.(type)
  }, [type])

  /**
   * Adopt an externally chosen variant without reporting it back as a user selection, which would
   * loop. `undefined` means the consumer is not controlling this at all (the PDP), as distinct
   * from `null`, which means it is controlling it and nothing is chosen.
   */
  useEffect(() => {
    if (value === undefined) return
    if (String(value) === String(selected.id)) return

    const match = findVariant(value)
    if (value && !match) return

    setSelected(match ? selectionFrom(match, type, productWidths.width) : emptySelection(productWidths.width))
  }, [value])

  /**
   * The [type] effect below rewrites selected.value from its own closure, which lands a render
   * late and left the header showing the previous unit. Derive what the header displays instead.
   */
  const selectedSizeLabel = useMemo(() => {
    if (!selected.id) return selected.value
    const currentSize = variants.find((v) => v.id === selected.id)
    if (!currentSize) return selected.value
    return handleGetTypeValue(type, currentSize) || selected.value
  }, [type, selected.id, selected.value])

  /** Both treatments take the same props, so the switch cannot introduce a behaviour difference. */
  const SizeOptions = layout === CONSOLIDATED_LAYOUT ? SelectSizeOptionsConsolidated : SelectSizeOptions

  /** Storewide tiers, so they cannot change while the component is mounted. */
  const stockIndicators = useMemo(() => getStockIndicators(), [])

  const ageCategory = useMemo(() => {
    const o = variants
    const validHeaders = checkHeader(o)
    const categories = sortBands(Array.from(new Set(o.map((a) => a?.age_group)).values()))
    /** Parse the variant data into their appropriate age groups and width sub-groups if present  */
    const groups = sizeGroups(o)
    return {
      categories,
      groups,
      validHeaders
    }
  }, [])

  /** The add-to-cart button sits outside this component, so it signals by event. */
  useEffect(() => {
    if (!sizeRequiredEvent) return undefined
    const handleSizeRequired = () => setSizeError(true)
    window.addEventListener(sizeRequiredEvent, handleSizeRequired)
    return () => window.removeEventListener(sizeRequiredEvent, handleSizeRequired)
  }, [sizeRequiredEvent])

  useEffect(() => {
    if (!selected.id || selected.firstRender) {
      handleSetSelected(selected)
      return
    }
    /** Get Current size */
    const currentSize = variants.find((v) => v.id === selected.id)
    if (!currentSize) return
    /** Parse in the size and the new type (EU, US, UK) to get the new parsed value. */
    const sizeValue = handleGetTypeValue(type, currentSize)
    /** Update the selected state */
    handleSetSelected({
      id: selected.id,
      name: selected.name,
      width: selected.width,
      variant: selected.variant,
      unavailable_stock: selected.unavailable_stock,
      value: !sizeValue ? selected.value : sizeValue
    })
  }, [type])

  return (
    <div
      className={
        className || 'border-t border-solid border-t-neutrals-light-grey bg-primary-white px-6 md:px-6 pb-4 pt-8'
      }
    >
      <SelectSizeHeader
        validHeaders={ageCategory.validHeaders}
        selected={selected}
        sizeLabel={selectedSizeLabel}
        type={type}
        callback={handleSetType}
        pageType={pageType}
        sizeError={sizeError}
        showSizeGuide={showSizeGuide}
      />
      <SizeOptions
        productId={productId}
        type={type}
        selected={selected}
        ageCategory={ageCategory}
        callback={handleSetSelected}
        stockIndicators={stockIndicators}
        linkedOptions={linkedOptions ?? {}}
      />
      {!!productWidths?.widthOptions?.length && productWidths.widthOptions.length > 1 && (
        <SelectWidthOptions
          widths={productWidths.widthOptions}
          handleSetSelected={handleSetSelected}
          selected={selected}
          type={type}
          productId={productId}
          parseSizeDataObjects={variants}
          showWidthAdvice={showWidthAdvice}
          textOverrides={optionOverrides}
        />
      )}
    </div>
  )
}
